import { create } from "zustand";
import * as api from "../lib/api";
import type { ApiCalendar, CreateCalendarInput, UpdateCalendarInput } from "../lib/types";

const VISIBILITY_KEY = "calendar-visibility";

function loadVisibility(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function saveVisibility(vis: Record<string, boolean>) {
  localStorage.setItem(VISIBILITY_KEY, JSON.stringify(vis));
}

interface CalendarState {
  calendars: ApiCalendar[];
  visibility: Record<string, boolean>;
  loading: boolean;
  fetch: () => Promise<void>;
  create: (input: CreateCalendarInput) => Promise<ApiCalendar>;
  update: (id: string, input: UpdateCalendarInput) => Promise<void>;
  remove: (id: string) => Promise<void>;
  toggleVisibility: (id: string) => void;
  isVisible: (id: string) => boolean;
}

export const useCalendarStore = create<CalendarState>((set, get) => ({
  calendars: [],
  visibility: loadVisibility(),
  loading: false,

  fetch: async () => {
    set({ loading: true });
    try {
      const calendars = await api.fetchCalendars();
      // Auto-show new calendars
      const vis = { ...get().visibility };
      for (const cal of calendars) {
        if (vis[cal.id] === undefined) vis[cal.id] = true;
      }
      saveVisibility(vis);
      set({ calendars, visibility: vis, loading: false });
    } catch {
      set({ loading: false });
    }
  },

  create: async (input: CreateCalendarInput) => {
    const calendar = await api.createCalendarApi(input);
    const vis = { ...get().visibility, [calendar.id]: true };
    saveVisibility(vis);
    set((s) => ({ calendars: [...s.calendars, calendar], visibility: vis }));
    return calendar;
  },

  update: async (id: string, input: UpdateCalendarInput) => {
    const calendar = await api.updateCalendarApi(id, input);
    set((s) => ({
      calendars: s.calendars.map((c) => (c.id === id ? calendar : c)),
    }));
  },

  remove: async (id: string) => {
    await api.deleteCalendarApi(id);
    const vis = { ...get().visibility };
    delete vis[id];
    saveVisibility(vis);
    set((s) => ({
      calendars: s.calendars.filter((c) => c.id !== id),
      visibility: vis,
    }));
  },

  toggleVisibility: (id: string) => {
    const vis = { ...get().visibility };
    vis[id] = !vis[id];
    saveVisibility(vis);
    set({ visibility: vis });
  },

  isVisible: (id: string) => get().visibility[id] !== false,
}));
